import { gql, NetworkStatus, useQuery } from "@apollo/client";
import debounce from "lodash/debounce";
import { useTranslations } from "next-intl";
import { useState } from "react";

import {
  HISTORY_FILTER_LIMIT,
  HOME_FOLDER_ID,
} from "@zapier/reporting/src/constants";
import { useSwrQuery } from "@zapier/reporting/src/hooks";
import type {
  FolderResultFragment as FolderResult,
  FoldersByIdQuery,
  FoldersByNameQuery,
} from "./useFolders.generated";

const folderFragment = gql`
  fragment FolderResult on Folder {
    id
    title
  }
`;

export const getFolders = gql`
  ${folderFragment}

  query FoldersByName(
    $accountId: ID!
    $limit: Int
    $offset: Int
    $title: String
  ) {
    account(id: $accountId) {
      id
      folders(limit: $limit, offset: $offset, title: $title) {
        edges {
          ...FolderResult
        }
        totalCount
      }
    }
  }
`;

export const getSelectedFolders = gql`
  ${folderFragment}

  query FoldersById($accountId: ID!, $ids: [ID!]!) {
    account(id: $accountId) {
      id
      folders(ids: $ids) {
        edges {
          ...FolderResult
        }
      }
    }
  }
`;

type Input = {
  accountId?: string;
  folderIds: string[] | undefined;
};

function useFolders({ accountId, folderIds = [] }: Input) {
  const t = useTranslations("history-page.filters.folder-filter");
  const [search, setSearch] = useState("");

  const homeFolder: FolderResult = {
    __typename: "Folder",
    id: HOME_FOLDER_ID,
    title: t("home-folder"),
  };
  const { client, data, error, fetchMore, loading, networkStatus } =
    useSwrQuery<FoldersByNameQuery>(getFolders, {
      notifyOnNetworkStatusChange: true,
      skip: !accountId,
      variables: {
        accountId,
        limit: HISTORY_FILTER_LIMIT,
        offset: 0,
        title: search,
      },
    });

  // Do not ask the server for the home folder since
  // we already have the definition for it and it will
  // 404 in the listing endpoint
  const nonHomeFolderIds = folderIds.filter(
    (id) => id !== homeFolder.id.toString()
  );

  const selectedFoldersResult = useQuery<FoldersByIdQuery>(getSelectedFolders, {
    skip: !accountId || nonHomeFolderIds.length === 0,
    variables: {
      accountId,
      ids: nonHomeFolderIds,
    },
  });

  const onSearch = debounce((inputValue: string) => {
    return setSearch(inputValue || "");
  }, 300);
  // Match the search against the translated title, so Home stays findable
  // in every locale rather than only when the user types "home".
  const includeHome = homeFolder.title
    .toLowerCase()
    .includes((search || "").toLowerCase());
  const folders = data?.account?.folders?.edges || [];
  const searchFolders = [includeHome && homeFolder, ...folders].filter(
    Boolean
  ) as FolderResult[];

  function getSelectedFoldersCache() {
    const serializedCache: Record<string, any> = client.cache.extract();
    return Object.values(serializedCache).filter((item) => {
      return (
        item.__typename === "Folder" && folderIds.includes(String(item.id))
      );
    }) as FolderResult[];
  }

  function onLoadMore() {
    fetchMore({
      updateQuery(prev, { fetchMoreResult }) {
        // We didn't get anything so nothing to update
        if (!fetchMoreResult) return prev;

        // Update the cache
        return {
          ...fetchMoreResult,
          account: {
            ...fetchMoreResult.account,
            folders: {
              ...fetchMoreResult?.account?.folders,
              edges: [
                ...(prev.account?.folders?.edges || []),
                ...(fetchMoreResult?.account?.folders?.edges || []),
              ],
            },
          },
        };
      },
      variables: {
        accountId,
        limit: HISTORY_FILTER_LIMIT,
        offset: folders.length,
        title: search,
      },
    });
  }

  let selectedFolders = selectedFoldersResult.loading
    ? getSelectedFoldersCache()
    : selectedFoldersResult?.data?.account?.folders?.edges || [];

  // Add home folder in
  if (folderIds.includes(homeFolder.id.toString())) {
    selectedFolders = [homeFolder, ...selectedFolders];
  }

  return {
    error,
    folders: searchFolders,
    isLoading: loading,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    onLoadMore:
      folders.length < (data?.account?.folders?.totalCount || 0)
        ? onLoadMore
        : undefined,
    onSearch,
    selectedFolders,
  };
}

export default useFolders;
